import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/stores/authStore";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { 
  History, Clock, CheckCircle2, XCircle, DollarSign, Globe, Shield, 
  ChevronRight, Eye, Loader2
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AuditHistory = ({ projectId, onClose }) => {
  const { token } = useAuth();
  const [audits, setAudits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedAudit, setSelectedAudit] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  useEffect(() => {
    fetchAuditHistory();
  }, [projectId]);

  const fetchAuditHistory = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${API}/projects/${projectId}/audit-history`,
        { headers: token ? { Authorization: `Bearer ${token}` } : {} }
      );
      setAudits(response.data);
    } catch (error) {
      toast.error("Failed to load audit history");
    } finally {
      setLoading(false);
    }
  };

  const viewAuditDetail = async (auditId) => {
    try {
      setDetailLoading(true);
      const response = await axios.get(
        `${API}/projects/${projectId}/audit-history/${auditId}`,
        { headers: token ? { Authorization: `Bearer ${token}` } : {} }
      );
      setSelectedAudit(response.data);
    } catch (error) {
      toast.error("Failed to load audit details");
    } finally {
      setDetailLoading(false);
    }
  };

  const formatDate = (isoString) => {
    if (!isoString) return "N/A";
    return new Date(isoString).toLocaleString();
  };

  const getScoreColor = (score) => {
    if (score >= 80) return "text-green-400";
    if (score >= 60) return "text-yellow-400";
    return "text-red-400";
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-sm bg-purple-500/10 border border-purple-500/30 flex items-center justify-center">
            <History className="w-5 h-5 text-purple-400" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-white">Audit History</h2>
            <p className="text-sm text-zinc-500">Track all audit runs for this project</p>
          </div>
        </div>
        <Button variant="outline" onClick={onClose} className="border-zinc-700 text-zinc-300">
          Close
        </Button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 text-[#00f0ff] animate-spin" />
        </div>
      ) : audits.length === 0 ? (
        <Card className="bg-[#09090b] border-white/5">
          <CardContent className="py-12 text-center">
            <History className="w-12 h-12 text-zinc-600 mx-auto mb-4" />
            <p className="text-zinc-400">No audit history yet</p>
            <p className="text-sm text-zinc-500">Run your first audit to see it here</p>
          </CardContent>
        </Card>
      ) : (
        <ScrollArea className="h-[500px]">
          <div className="space-y-3 pr-4">
            {audits.map((audit, index) => (
              <Card 
                key={audit.id}
                className="bg-[#09090b] border-white/5 hover:border-white/10 transition-colors cursor-pointer"
                onClick={() => viewAuditDetail(audit.id)}
                data-testid={`audit-history-item-${index}`}
              >
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className={`text-3xl font-bold ${getScoreColor(audit.resilience_score)}`}>
                        {audit.resilience_score}
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium text-white capitalize">
                            {audit.audit_type.replace("-", " ")}
                          </span>
                          <span className={`px-2 py-0.5 rounded text-xs ${
                            audit.finops?.budget_status === "PASS" 
                              ? "bg-green-500/20 text-green-400" 
                              : "bg-red-500/20 text-red-400"
                          }`}>
                            {audit.finops?.budget_status || "N/A"}
                          </span>
                        </div>
                        <div className="flex items-center gap-1 text-xs text-zinc-500 mt-1">
                          <Clock className="w-3 h-3" />
                          {formatDate(audit.created_at)}
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-6">
                      <div className="flex items-center gap-4 text-sm">
                        <div className="flex items-center gap-1">
                          {audit.resilience?.multi_az ? (
                            <CheckCircle2 className="w-4 h-4 text-green-400" />
                          ) : (
                            <XCircle className="w-4 h-4 text-red-400" />
                          )}
                          <span className="text-zinc-400">Multi-AZ</span>
                        </div>
                        <div className="flex items-center gap-1">
                          {audit.compliance?.encryption_at_rest ? (
                            <CheckCircle2 className="w-4 h-4 text-green-400" />
                          ) : (
                            <XCircle className="w-4 h-4 text-red-400" />
                          )}
                          <span className="text-zinc-400">Encrypted</span>
                        </div>
                      </div>
                      <ChevronRight className="w-5 h-5 text-zinc-600" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </ScrollArea>
      )}

      {/* Audit Detail Modal */}
      <Dialog open={!!selectedAudit} onOpenChange={() => setSelectedAudit(null)}>
        <DialogContent className="bg-[#09090b] border-white/10 max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Eye className="w-5 h-5 text-[#00f0ff]" />
              Audit Details
            </DialogTitle>
          </DialogHeader>
          
          {detailLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 text-[#00f0ff] animate-spin" />
            </div>
          ) : selectedAudit && (
            <div className="space-y-6 mt-4">
              {/* Score and Date */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className={`text-5xl font-bold ${getScoreColor(selectedAudit.resilience_score)}`}>
                    {selectedAudit.resilience_score}
                  </div>
                  <div className="text-zinc-400">/ 100</div>
                </div>
                <div className="text-sm text-zinc-500">
                  {formatDate(selectedAudit.created_at)}
                </div>
              </div>

              {/* FinOps */}
              <Card className="bg-black/30 border-white/5">
                <CardHeader className="pb-2">
                  <CardTitle className="text-green-400 text-sm flex items-center gap-2">
                    <DollarSign className="w-4 h-4" />
                    FinOps Cost Auditor
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-zinc-400">Budget Status</span>
                    <span className={selectedAudit.finops?.budget_status === "PASS" ? "text-green-400" : "text-red-400"}>
                      {selectedAudit.finops?.budget_status || "N/A"}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-zinc-400">Estimated Cost</span>
                    <span className="text-white">{selectedAudit.finops?.estimated_cost || "N/A"}</span>
                  </div>
                  {selectedAudit.finops?.tips?.length > 0 && (
                    <div className="pt-2 border-t border-white/5">
                      <span className="text-xs text-zinc-500">Tips:</span>
                      <ul className="mt-1 space-y-1">
                        {selectedAudit.finops.tips.map((tip, i) => (
                          <li key={i} className="text-xs text-zinc-400">• {tip}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Resilience */}
              <Card className="bg-black/30 border-white/5">
                <CardHeader className="pb-2">
                  <CardTitle className="text-cyan-400 text-sm flex items-center gap-2">
                    <Globe className="w-4 h-4" />
                    Global Resilience Audit
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  {["multi_az", "auto_scaling", "backup"].map((key) => (
                    <div key={key} className="flex justify-between items-center">
                      <span className="text-zinc-400 capitalize">{key.replace("_", " ")}</span>
                      {selectedAudit.resilience?.[key] ? (
                        <CheckCircle2 className="w-4 h-4 text-green-400" />
                      ) : (
                        <XCircle className="w-4 h-4 text-red-400" />
                      )}
                    </div>
                  ))}
                  {selectedAudit.resilience?.spof?.length > 0 && (
                    <div className="pt-2 border-t border-white/5">
                      <span className="text-xs text-zinc-500">Single Points of Failure:</span>
                      <ul className="mt-1 space-y-1">
                        {selectedAudit.resilience.spof.map((item, i) => (
                          <li key={i} className="text-xs text-yellow-400">⚠ {item}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Compliance */}
              <Card className="bg-black/30 border-white/5">
                <CardHeader className="pb-2">
                  <CardTitle className="text-purple-400 text-sm flex items-center gap-2">
                    <Shield className="w-4 h-4" />
                    Compliance Gate
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  {["encryption_at_rest", "private_networking", "iam_least_privilege"].map((key) => (
                    <div key={key} className="flex justify-between items-center">
                      <span className="text-zinc-400 capitalize">{key.replace(/_/g, " ")}</span>
                      {selectedAudit.compliance?.[key] ? (
                        <CheckCircle2 className="w-4 h-4 text-green-400" />
                      ) : (
                        <XCircle className="w-4 h-4 text-red-400" />
                      )}
                    </div>
                  ))}
                  <div className="flex justify-between">
                    <span className="text-zinc-400">Critical Issues</span>
                    <span className={selectedAudit.compliance?.critical_count === 0 ? "text-green-400" : "text-red-400"}>
                      {selectedAudit.compliance?.critical_count || 0}
                    </span>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
